import { useCallback } from 'react';
import { useFieldArray } from 'react-hook-form';
/**
 * `useListEditing` is a custom hook designed to manage the editing, adding, and removing of items within a list field of a form.
 * It is intended to work seamlessly with 'react-hook-form' for form control and state management.
 * This hook simplifies the process of handling list operations, making it easier to add, edit, and remove items within the list field.
 *
 * @template T - The type of data for the list items.
 * @param {UseListEditingProps<T>} props - The properties passed to the useListEditing hook. These include:
 * @param {ArrayPath<T>} listPath - The path to the list field in the form.
 * @param {Control<T>} control - The control object from 'react-hook-form' used for managing the state of form fields.
 *
 * @returns {UseListEditingReturn<T>} - An object containing functions and properties for managing the list:
 * - {T[]} fields - The current list of items.
 * - {(item: T) => void} append - Function to add a new item to the list.
 * - {(index: number) => void} remove - Function to remove an item from the list by its index.
 * - {(index: number, item: T) => void} update - Function to update an item in the list by its index.
 *
 * Note: This hook requires 'react-hook-form' for form control and state management.
 */
export const useListEditing = ({ listPath, control }) => {
    const { fields, append: appendItem, remove: removeItem, update: updateItem } = useFieldArray({
        control,
        name: listPath
    });
    // possible improvement - type assertion if possible
    const append = useCallback((item) => appendItem(item), [appendItem]);
    const remove = useCallback((index) => removeItem(index), [removeItem]);
    // possible improvement - type assertion if possible
    const update = useCallback((index, item) => {
        updateItem(index, item);
    }, [updateItem]);
    return { fields, append, remove, update };
};
